import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, Sparkles, Sun, AlertTriangle, ShoppingCart, ListChecks } from "lucide-react";

const SUGGESTIONS = [
  "Come sta andando oggi?", "Quanto contanti e quanto carta?", "Scontrino medio?",
  "Quali prodotti vendo di più?", "Quali ingredienti stanno finendo?", "Cosa scade nei prossimi 7 giorni?",
  "Ci sono differenze di cassa?", "Quanti sconti abbiamo fatto?", "Ci sono stati molti storni?",
];

export default function DoganaAI() {
  const [brief, setBrief] = useState(null);
  const [messages, setMessages] = useState([{ role: "ai", text: "Ciao! Sono Dogana AI. Chiedimi come sta andando il locale — rispondo solo con dati reali dal gestionale." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { api.get("/ai/manager/brief").then(({ data }) => setBrief(data)).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const ask = async (q) => {
    const question = (q ?? input).trim();
    if (!question || busy) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/ai/manager/chat", { message: question });
      setMessages((m) => [...m, { role: "ai", text: data.answer, tool: data.tool }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: formatApiError(e.response?.data?.detail) || "AI non disponibile al momento." }]);
    } finally { setBusy(false); }
  };

  const suggestDraft = async () => {
    try { const { data } = await api.post("/ai/manager/purchase-draft/suggest"); setDraft(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const createDraft = async () => {
    try { await api.post("/ai/manager/purchase-draft", { items: draft.items, reason: draft.reason }); toast.success("Bozza d'ordine creata"); setDraft(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const briefIcon = (t) => (t === "reservations" ? Sun : t === "food_cost" ? AlertTriangle : AlertTriangle);

  return (
    <div data-testid="dogana-ai-page">
      <PageHeader title="Dogana AI" subtitle="Assistente gestionale · numeri reali dal tuo locale" testid="ai-header">
        <Link to="/dogana-ai/azioni"><Button variant="outline" className="rounded-full" data-testid="ai-open-actions"><ListChecks className="w-4 h-4 mr-1" /> Azioni</Button></Link>
        <Button variant="outline" className="rounded-full" onClick={suggestDraft} data-testid="ai-suggest-draft"><ShoppingCart className="w-4 h-4 mr-1" /> Bozza riordino</Button>
      </PageHeader>

      {brief && brief.insights?.length > 0 && (
        <Card className="p-4 rounded-xl mb-4" data-testid="ai-morning-brief">
          <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-[#C9A24B]" /><span className="font-semibold text-sm">Dogana AI — Oggi</span></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {brief.insights.map((i, k) => {
              const Ic = briefIcon(i.type);
              return <div key={k} className="flex items-start gap-2 text-sm rounded-lg bg-secondary/60 p-2" data-testid={`brief-${i.type}`}><Ic className="w-4 h-4 mt-0.5 text-[#C9A24B] shrink-0" /><span>{i.text}</span></div>;
            })}
          </div>
        </Card>
      )}

      <Card className="rounded-xl flex flex-col h-[60vh]" data-testid="ai-chat">
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((m, k) => (
            <div key={k} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "bg-[#14110E] text-white" : "bg-secondary"}`} data-testid={`msg-${m.role}`}>{m.text}</div>
              {m.role === "ai" && m.tool === "get_supplier_spend" && (
                <p className="max-w-[85%] text-[11px] text-amber-700 mt-1 flex items-start gap-1" data-testid="supplier-credit-note-warning"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />Nota: la spesa da fornitore non nettizza ancora note di credito / resi fornitore.</p>
              )}
            </div>
          ))}
          {busy && <div className="flex justify-start"><div className="bg-secondary rounded-2xl px-3 py-2"><Loader2 className="w-4 h-4 animate-spin" /></div></div>}
          <div ref={endRef} />
        </div>
        <div className="border-t border-border p-2 flex flex-wrap gap-1.5">
          {SUGGESTIONS.map((s) => <button key={s} onClick={() => ask(s)} className="text-xs px-2.5 py-1 rounded-full border border-border hover:bg-secondary" data-testid="ai-suggestion">{s}</button>)}
        </div>
        <div className="border-t border-border p-3 flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder="Chiedi qualcosa…" data-testid="ai-input" />
          <Button className="rounded-full" onClick={() => ask()} disabled={busy} data-testid="ai-send"><Send className="w-4 h-4" /></Button>
        </div>
      </Card>

      {draft && (
        <Card className="p-4 rounded-xl mt-4 border-[#C9A24B]" data-testid="ai-draft-card">
          <p className="font-semibold text-sm mb-1">Bozza riordino proposta</p>
          <p className="text-xs text-muted-foreground mb-2">{draft.reason}</p>
          <div className="space-y-1 mb-3">
            {draft.items.length === 0 && <p className="text-sm text-muted-foreground">Nessun articolo sotto scorta.</p>}
            {draft.items.map((i, k) => <div key={k} className="text-sm flex justify-between"><span>{i.name}</span><span>{i.quantity} {i.unit}</span></div>)}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" className="rounded-full" onClick={() => setDraft(null)} data-testid="draft-cancel">Annulla</Button>
            <Button className="rounded-full" onClick={createDraft} disabled={draft.items.length === 0} data-testid="draft-create">Crea bozza</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
